/**
 * Phase H (Δ-13) — Search field.
 *
 * CR-0014b polish (Δ-13b):
 *  - Desktop input + mobile-expanded input → `rounded-md` (matches Reset).
 *  - Mobile collapsed icon STAYS `rounded-full` (a circle — per spec).
 *
 * Mobile expand state is hoisted to `<AppBar>` via `mobileExpanded` +
 * `onToggleMobile` props (Δ-13a).
 *
 * CR-0014c (Δ-13c, 2026-05-30) — pre-breakfast width variants (C-25):
 *  - Desktop / tablet: pre-breakfast width is `w-full max-w-md` (~448px
 *    centred via parent flex). Post-breakfast: `w-[144px]` (unchanged).
 *  - Mobile: pre-breakfast renders the always-expanded `flex-1` input
 *    directly. The icon-collapse interaction (state machine in
 *    <AppBar>) is skipped pre-breakfast because <ModeTabs /> is hidden
 *    and the input has the whole row to itself. Post-breakfast: the
 *    existing collapsed-icon ↔ expanded-input state machine resumes.
 */
import { Search, X } from "lucide-react";
import useAppStore from "@/store/useAppStore";

export default function SearchField({ mobileExpanded, onToggleMobile }) {
  const breakfastStarted = useAppStore((s) => s.appState?.breakfast_started);
  const searchQuery = useAppStore((s) => s.searchQuery);
  const setSearchQuery = useAppStore((s) => s.setSearchQuery);

  const handleClearAndCollapse = () => {
    setSearchQuery("");
    if (onToggleMobile) onToggleMobile(false);
  };

  // Δ-13c: width variant. Pre-breakfast grows to fill the row (capped
  // at max-w-md on desktop). Post-breakfast keeps the compact 144px form
  // so it sits inline with tabs and chips.
  const desktopBoxClass = breakfastStarted
    ? "w-[144px] h-[26px]"
    : "w-full max-w-md h-[28px]";

  return (
    <>
      {/* Desktop / tablet — always inline. rounded-md per Δ-13b. */}
      <div
        data-testid="search-field-desktop"
        className={`hidden md:flex items-center gap-2 rounded-md border px-3 py-1 ${desktopBoxClass}`}
        style={{ borderColor: "#E0E0E0", backgroundColor: "#FAFAFA" }}
      >
        <Search size={12} style={{ color: "#9CA3AF" }} />
        <input
          data-testid="search-input-desktop"
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={
            breakfastStarted ? "Search…" : "Search categories or table codes…"
          }
          className="flex-1 min-w-0 bg-transparent text-xs outline-none"
          style={{ color: "#424242" }}
        />
        {searchQuery && (
          <button
            data-testid="search-clear-desktop"
            onClick={() => setSearchQuery("")}
            className="rounded-full p-0.5 hover:bg-gray-200 shrink-0"
            aria-label="Clear search"
          >
            <X size={11} style={{ color: "#6B7280" }} />
          </button>
        )}
      </div>

      {/* Mobile, pre-breakfast (Δ-13c): always-expanded input, no
          icon-collapse. <AppBar> mobile row 2 hosts only this element
          because <ModeTabs /> is hidden. */}
      {!breakfastStarted && (
        <div
          data-testid="search-field-mobile-prebk"
          className="md:hidden flex items-center gap-2 rounded-md border px-3 py-1 flex-1 min-w-0 h-[28px]"
          style={{ borderColor: "#E0E0E0", backgroundColor: "#FAFAFA" }}
        >
          <Search size={13} style={{ color: "#9CA3AF" }} />
          <input
            data-testid="search-input-mobile-prebk"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search categories or table codes…"
            className="flex-1 min-w-0 bg-transparent text-xs outline-none"
            style={{ color: "#424242" }}
          />
          {searchQuery && (
            <button
              data-testid="search-clear-mobile-prebk"
              onClick={() => setSearchQuery("")}
              className="rounded-full p-0.5 hover:bg-gray-200 shrink-0"
              aria-label="Clear search"
            >
              <X size={13} style={{ color: "#6B7280" }} />
            </button>
          )}
        </div>
      )}

      {/* Mobile, post-breakfast — collapsed circular icon (stays rounded-full). */}
      {breakfastStarted && !mobileExpanded && (
        <button
          data-testid="search-icon-mobile"
          onClick={() => onToggleMobile && onToggleMobile(true)}
          className="md:hidden flex items-center justify-center rounded-full shrink-0"
          style={{
            width: 28,
            height: 28,
            border: "1px solid #E0E0E0",
            backgroundColor: "#FAFAFA",
            color: "#424242",
          }}
          aria-label="Open search"
        >
          <Search size={14} style={{ color: "#424242" }} />
        </button>
      )}

      {/* Mobile, post-breakfast expanded — fills row. rounded-md per Δ-13b. */}
      {breakfastStarted && mobileExpanded && (
        <div
          data-testid="search-field-mobile"
          className="md:hidden flex items-center gap-2 rounded-md border px-3 py-1 flex-1 min-w-0 h-[28px]"
          style={{ borderColor: "#E0E0E0", backgroundColor: "#FAFAFA" }}
        >
          <Search size={13} style={{ color: "#9CA3AF" }} />
          <input
            autoFocus
            data-testid="search-input-mobile"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tables…"
            className="flex-1 min-w-0 bg-transparent text-xs outline-none"
            style={{ color: "#424242" }}
          />
          <button
            data-testid="search-collapse-mobile"
            onClick={handleClearAndCollapse}
            className="rounded-full p-0.5 hover:bg-gray-200 shrink-0"
            aria-label="Close search"
          >
            <X size={13} style={{ color: "#6B7280" }} />
          </button>
        </div>
      )}
    </>
  );
}
